import React, { useState, useEffect } from 'react';
import { fxRatesApi } from '../services/api';
import '../styles/Panel.css';

function FXRatesPanel() {
  const [startDate, setStartDate] = useState('2024-04-01');
  const [endDate, setEndDate] = useState('2025-03-31');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // List of available FX rates files
  const [fxRatesList, setFxRatesList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);

  // Load available FX rates files on component mount
  useEffect(() => {
    loadFXRatesList();
  }, []);

  const loadFXRatesList = async () => {
    setListLoading(true);
    setListError(null);
    try {
      const data = await fxRatesApi.listFXRates();
      setFxRatesList(data.files || []);
    } catch (err) {
      console.error('Error loading FX rates list:', err);
      setListError(err.message);
    } finally {
      setListLoading(false);
    }
  };

  const handleFetchFXRates = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await fxRatesApi.fetchFXRates(startDate, endDate);
      setResult(data);
      // Reload the list to show the new file
      await loadFXRatesList();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (period, timestamp) => {
    setDownloadingFile(`${period}_${timestamp}`);
    try {
      const blob = await fxRatesApi.downloadFXRates(period, timestamp);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fx_rates_${period}_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading FX rates:', err);
      alert(`Error downloading file: ${err.message}`);
    } finally {
      setDownloadingFile(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="panel fx-rates-panel">
      <h2>FX Rates Management</h2>

      <div className="alert alert-info" style={{ marginBottom: '20px', backgroundColor: '#e3f2fd', borderColor: '#2196f3', color: '#1565c0' }}>
        <strong>Note:</strong> This section fetches FX (Foreign Exchange) rates from <strong>all enabled SAP entities</strong> (not entity-specific).
        Data is stored in a common location and consolidated into a single Excel file with separate sheets for each entity.
      </div>

      <div className="section">
        <h3>Fetch New FX Rates</h3>

        <div className="form-group">
          <label>Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label>End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={loading}
          />
        </div>

        <button
          className="btn btn-primary"
          onClick={handleFetchFXRates}
          disabled={loading || !startDate || !endDate}
        >
          {loading ? 'Fetching FX Rates from All Entities...' : 'Fetch FX Rates'}
        </button>

        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {result && result.success && (
          <div className="alert alert-success">
            <h3>✓ FX Rates Fetched Successfully</h3>
            <div className="result-details">
              <p><strong>Period:</strong> {result.start_date} to {result.end_date}</p>
              <p><strong>Total Entities:</strong> {result.total_entities}</p>
              <p><strong>Total Records:</strong> {result.total_records?.toLocaleString()}</p>
              <p><strong>Timestamp:</strong> {result.timestamp}</p>
              <p><strong>S3 Path:</strong> <code>{result.s3_path}</code></p>

              {result.entities && result.entities.length > 0 && (
                <div style={{ marginTop: '15px' }}>
                  <h4>Entity Summary:</h4>
                  <table className="data-table" style={{ width: '100%', marginTop: '10px' }}>
                    <thead>
                      <tr>
                        <th>Entity</th>
                        <th>Company Code</th>
                        <th>Records</th>
                        <th>Currencies</th>
                        <th>Date Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.entities.map((entity, idx) => (
                        <tr key={idx}>
                          <td>{entity.description}</td>
                          <td>{entity.finalyzer_company_code}</td>
                          <td>{entity.record_count?.toLocaleString()}</td>
                          <td>{entity.unique_currencies}</td>
                          <td>{entity.unique_dates}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="section" style={{ marginTop: '30px' }}>
        <h3>Available FX Rates Files</h3>

        <button
          className="btn btn-secondary"
          onClick={loadFXRatesList}
          disabled={listLoading}
          style={{ marginBottom: '15px' }}
        >
          {listLoading ? 'Refreshing...' : 'Refresh List'}
        </button>

        {listError && (
          <div className="alert alert-error">
            <strong>Error:</strong> {listError}
          </div>
        )}

        {listLoading ? (
          <p>Loading FX rates files...</p>
        ) : fxRatesList.length === 0 ? (
          <p className="help-text">No FX rates files found. Generate your first file above.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Entities</th>
                  <th>Total Records</th>
                  <th>Generated At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fxRatesList.map((file, idx) => (
                  <tr key={idx}>
                    <td><code>{file.period}</code></td>
                    <td>{file.start_date || 'N/A'}</td>
                    <td>{file.end_date || 'N/A'}</td>
                    <td>{file.total_entities || 'N/A'}</td>
                    <td>{file.total_records ? file.total_records.toLocaleString() : 'N/A'}</td>
                    <td>{formatDate(file.upload_time)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleDownload(file.period, file.timestamp)}
                        disabled={downloadingFile === `${file.period}_${file.timestamp}`}
                      >
                        {downloadingFile === `${file.period}_${file.timestamp}` ? 'Downloading...' : 'Download Excel'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default FXRatesPanel;
